import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { shopkeepersAPI, TokenManager } from '../api';
import useGeolocation from '../hooks/useGeolocation';
import QrScan from '../components/QrScan';
import QRCard from '../components/QRCard';
import type { Shopkeeper } from '../types/shopkeepers';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  LogOut,
  User,
  Store,
  Clock,
  IndianRupee,
  FileText,
  QrCode,
  Camera,
  BarChart3,
  ChevronRight,
  Printer
} from 'lucide-react';

type ProfileData = Shopkeeper;

const Profile = () => {
  const navigate = useNavigate();
  const { refreshUser, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'profile' | 'qr' | 'scan' | 'stats'>('profile');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['profile', 'qr', 'scan', 'stats'].includes(tab)) {
      if (tab === 'scan' && window.innerWidth >= 1024) {
        setActiveTab('profile');
      } else {
        setActiveTab(tab as any);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop && activeTab === 'scan') {
        setActiveTab('profile');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab]);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const {
    location,
    error: locationError,
    getCurrentLocation,
    reverseGeocode,
    isLoading: locationLoading,
    isSuccess: locationSuccess,
    isDenied: locationDenied,
    hasError: locationHasError
  } = useGeolocation();

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (location.lat && location.lng) {
      setValue('latitude', location.lat);
      setValue('longitude', location.lng);

      if (!watch('shop_address')) {
        reverseGeocode(location.lat, location.lng).then(addressData => {
          if (addressData) {
            setValue('shop_address', addressData.formatted);
            setValue('city', addressData.city);
            setValue('state', addressData.state);
          }
        });
      }
    }
  }, [location, setValue, watch, reverseGeocode]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await shopkeepersAPI.getProfile();
      setProfileData(response);
      reset(response);
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleGetLocation = async () => {
    try {
      await getCurrentLocation();
    } catch (err) {
      console.error('Failed to get location');
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setSaving(true);
      setError('');
      setSuccess(false);

      await shopkeepersAPI.updateProfile(data);
      setSuccess(true);
      await loadProfile();
      await refreshUser();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleQRScan = (qrData: string) => {
    try {
      if (qrData.includes('shop=')) {
        const shopId = qrData.split('shop=')[1].split('&')[0];
        navigate(`/send?shop=${shopId}`);
      } else {
        window.location.href = qrData;
      }
    } catch (err) {
      setError('Invalid QR code format');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 font-black uppercase tracking-widest text-[10px]">Loading Profile</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24">
      {/* Native-Style Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 pt-safe-top">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-600 active:scale-95 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center">
             <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-2 shadow-lg shadow-blue-500/20">
                <Printer className="w-4 h-4 text-white" />
             </div>
             <h1 className="text-sm font-black uppercase tracking-widest text-slate-900">Account</h1>
          </div>

          <button
            onClick={handleLogout}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 active:scale-95 transition-all"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-6">
        {/* Profile Card Summary */}
        <div className="bg-white rounded-[2.5rem] p-6 shadow-xl shadow-slate-200/50 border border-slate-100 mb-8 flex items-center space-x-5">
           <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center text-white text-3xl font-black shadow-xl shadow-blue-200">
              {(profileData?.shop_name || profileData?.full_name || 'U').charAt(0).toUpperCase()}
           </div>
           <div className="flex-1 min-w-0">
              <h2 className="text-xl font-black text-slate-900 tracking-tight truncate">
                 {profileData?.shop_name || 'My Profile'}
              </h2>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest truncate">
                 {profileData?.full_name}
              </p>
              <div className="flex items-center mt-2">
                 <div className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${profileData?.is_open ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                    {profileData?.is_open ? 'Shop Open' : 'Shop Closed'}
                 </div>
              </div>
           </div>
        </div>

        {/* Premium Native Tabs */}
        <div className="flex p-1.5 bg-slate-200/50 backdrop-blur-md rounded-[1.5rem] mb-8">
           {[
             { id: 'profile', icon: Store, label: 'Shop' },
             { id: 'qr', icon: QrCode, label: 'My QR' },
             ...(!isDesktop ? [{ id: 'scan', icon: Camera, label: 'Scan' }] : []),
             { id: 'stats', icon: BarChart3, label: 'Stats' }
           ].map((tab) => (
             <button
               key={tab.id}
               onClick={() => setActiveTab(tab.id as any)}
               className={`flex-1 flex flex-col items-center justify-center py-3 rounded-[1.2rem] transition-all duration-300 ${activeTab === tab.id ? 'bg-white text-blue-600 shadow-lg' : 'text-slate-500 hover:text-slate-700'}`}
             >
               <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'scale-110' : ''}`} />
               <span className="text-[10px] font-black uppercase tracking-tight mt-1">{tab.label}</span>
             </button>
           ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'profile' && (
            <motion.div
              key="profile-tab"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-6"
            >
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {success && (
                  <div className="bg-green-50 border border-green-100 text-green-700 p-4 rounded-2xl flex items-center text-xs font-bold animate-fadeIn">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Profile Updated Successfully!
                  </div>
                )}

                <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 space-y-5">
                   <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 flex items-center">
                      <User className="w-3.5 h-3.5 mr-2" />
                      Business Information
                   </h3>
                   
                   <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Shop Name</label>
                        <input {...register('shop_name', { required: true })} className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-sm font-bold" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Contact Phone</label>
                        <input {...register('phone')} type="tel" className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-sm font-bold" />
                      </div>
                   </div>
                </div>

                <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 space-y-5">
                   <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 flex items-center">
                      <MapPin className="w-3.5 h-3.5 mr-2" />
                      Address Details
                   </h3>
                   <div className="space-y-4">
                      <textarea {...register('shop_address')} rows={3} className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-sm font-medium" placeholder="Full Shop Address" />
                      
                      <button type="button" onClick={handleGetLocation} className="w-full bg-blue-50 text-blue-600 font-black py-3.5 rounded-2xl text-[10px] uppercase tracking-widest flex items-center justify-center border border-blue-100 active:scale-95 transition-all">
                         <MapPin className="w-3.5 h-3.5 mr-2" />
                         {locationLoading ? 'Updating Location...' : 'Refresh GPS Location'}
                      </button>
                   </div>
                </div>

                <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 space-y-5">
                   <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 flex items-center">
                      <Clock className="w-3.5 h-3.5 mr-2" />
                      Pricing & Status
                   </h3>
                   <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">B&W (₹)</label>
                        <input {...register('price_per_page_bw')} type="number" step="0.5" className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-sm font-bold" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Color (₹)</label>
                        <input {...register('price_per_page_color')} type="number" step="1" className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-sm font-bold" />
                      </div>
                   </div>
                   
                   <label className="flex items-center p-4 bg-slate-50 rounded-2xl cursor-pointer active:scale-95 transition-all">
                      <input {...register('is_open')} type="checkbox" className="w-5 h-5 rounded-lg border-2 border-slate-300 text-blue-600 focus:ring-blue-500" />
                      <span className="ml-3 text-sm font-black text-slate-700 uppercase tracking-tight">Accepting Orders</span>
                   </label>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-slate-900 text-white py-5 px-6 rounded-[2rem] font-black uppercase tracking-[0.2em] text-sm shadow-xl shadow-slate-200 active:scale-95 transition-all"
                >
                  {saving ? 'Saving...' : 'Update Profile'}
                </button>
              </form>
            </motion.div>
          )}

          {activeTab === 'qr' && (
            <motion.div
              key="qr-tab"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full"
            >
              <QRCard shopId={profileData?.public_id || ''} shopName={profileData?.shop_name || ''} />
            </motion.div>
          )}

          {activeTab === 'scan' && (
            <motion.div
              key="scan-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-[2.5rem] p-6 shadow-sm border border-slate-100"
            >
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Camera className="w-4 h-4 mr-2 text-blue-600" />
                Inter-Shop Transfer
              </h3>
              <p className="text-xs text-slate-500 font-medium mb-6 leading-relaxed">
                Scan another shop's QR code to transfer or view their services.
              </p>
              <div className="rounded-[2rem] overflow-hidden">
                 <QrScan onScan={handleQRScan} />
              </div>
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <motion.div
              key="stats-tab"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                 <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 rounded-[2rem] text-white shadow-xl shadow-blue-100">
                    <FileText className="w-6 h-6 mb-3 opacity-50" />
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Total Files</p>
                    <p className="text-3xl font-black tabular-nums">{Math.max(profileData?.total_documents_received || 0, profileData?.total_documents_printed || 0)}</p>
                 </div>
                 <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                    <CheckCircle2 className="w-6 h-6 mb-3 text-green-500" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Printed</p>
                    <p className="text-3xl font-black tabular-nums text-slate-900">{profileData?.total_documents_printed || 0}</p>
                 </div>
              </div>
              
              <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm flex items-center justify-between">
                 <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center">
                       <BarChart3 className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                       <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Customer Rating</p>
                       <p className="text-xl font-black text-slate-900">{profileData?.customer_rating?.toFixed(1) || 'N/A'}/5.0</p>
                    </div>
                 </div>
                 <div className="flex items-center space-x-1">
                    {[1,2,3,4,5].map(i => (
                       <div key={i} className={`w-1.5 h-1.5 rounded-full ${i <= (profileData?.customer_rating || 0) ? 'bg-amber-400' : 'bg-slate-200'}`}></div>
                    ))}
                 </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Profile;
